import { FC, memo, useMemo, useId, useRef, useCallback } from 'react'
import classNames from 'classnames'
import { Popover } from 'devextreme-react'
import { NavLink } from 'react-router-dom'

import { hashCode } from '@crew/utils'

import { CrewGuideToolTip } from 'components/devextreme/crewGuideToolTip/crewGuideToolTip'

import {
  CrewRetryImage,
  CrewRetryImageRetryType,
} from '../crewRetryImage/crewRetryImage'

import CrewLock from 'assets/icons/crewLock.svg'

// アバターの形状
export const CrewAvatarShapeType = {
  Circle: 'circle',
  Square: 'square',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type CrewAvatarShapeType =
  (typeof CrewAvatarShapeType)[keyof typeof CrewAvatarShapeType]

// アバターのサイズ
export const CrewAvatarSize = {
  base: 'base',
  '3xs': '3xs',
  '2xs': '2xs',
  xs: 'xs',
  sm: 'sm',
  lg: 'lg',
  xl: 'xl',
  '2xl': '2xl',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type CrewAvatarSize =
  (typeof CrewAvatarSize)[keyof typeof CrewAvatarSize]

type avatarProps = {
  displayName: string
  avatarImage?: string
  className?: string
  imageURL?: string
  shape?: CrewAvatarShapeType // 未指定の場合は、丸型がデフォルト
  size?: CrewAvatarSize // 未指定の場合は、baseがデフォルト
  isPrivate?: boolean
  cacheValue: string | number // キャッシュ対策用。基本は「ID+version」を指定。プロジェクト登録ダイアログのアバター初期表示時など明示的なentityIdがない場合は表示名等を指定する。
  redirectUrl?: string // url when click avatar
  showPresenceState?: boolean
  presenceStateMessage?: string // 在席状態メッセージ
  hideTooltip?: boolean // ツールチップを非表示にするか
}

export const CrewAvatar: FC<avatarProps> = memo((props) => {
  // popoverのtarget指定のために useId で一意な値を生成する
  // https://ja.react.dev/reference/react/useId
  // useId()で生成されるIDにはコロン(:)が含まれるため、CSSセレクターとして安全な形式に変換しておく
  const popoverTargetId = useId().replace(/:/g, '_')

  const popoverRef = useRef<Popover>(null)

  const targetSizeClassNames = useMemo(() => {
    if (props.size === CrewAvatarSize.base || props.size === undefined) {
      return 'crew-avatar'
    } else if (props.size === CrewAvatarSize['3xs']) {
      return 'crew-avatar-3xs'
    } else if (props.size === CrewAvatarSize['2xs']) {
      return 'crew-avatar-2xs'
    } else if (props.size === CrewAvatarSize.xs) {
      return 'crew-avatar-xs'
    } else if (props.size === CrewAvatarSize.sm) {
      return 'crew-avatar-sm'
    } else if (props.size === CrewAvatarSize.lg) {
      return 'crew-avatar-lg'
    } else if (props.size === CrewAvatarSize.xl) {
      return 'crew-avatar-xl'
    } else if (props.size === CrewAvatarSize['2xl']) {
      return 'crew-avatar-2xl'
    } else {
      return ''
    }
  }, [props.size])

  const targetShapeClassNames = useMemo(() => {
    if (
      props.shape === CrewAvatarShapeType.Circle ||
      props.shape === undefined
    ) {
      // 丸型
      return 'rounded-full'
    } else if (props.shape === CrewAvatarShapeType.Square) {
      // 四角型
      return 'rounded-md'
    } else {
      return ''
    }
  }, [props.shape])

  const targetSrc = useMemo(() => {
    if (!props.imageURL) {
      return ''
    }

    // キャッシュ対策用の値が数値の場合は文字列に変換
    const cacheValue =
      typeof props.cacheValue === 'string'
        ? props.cacheValue
        : props.cacheValue.toString()

    // 画像URLにキャッシュ対策用のクエリパラメータを付与
    return `${props.imageURL}${props.imageURL.includes('?') ? '&' : '?'}cache=${hashCode(cacheValue)}`
  }, [props.imageURL, props.cacheValue])

  // ツールチップの表示内容
  const tooltipText = useMemo(
    () =>
      props.showPresenceState && props.presenceStateMessage
        ? `${props.displayName} / ${props.presenceStateMessage}` // 在席状態表示ありの場合：「表示名 / 在席状態メッセージ」
        : props.displayName, // 在席状態表示なしの場合：表示名のみ
    [props.showPresenceState, props.displayName, props.presenceStateMessage]
  )

  // アバタークリック時にpopoverを閉じる
  const handleClickAvatar = useCallback(() => {
    if (popoverRef.current) {
      popoverRef.current.instance.hide()
    }
  }, [])

  // ツールチップを表示するか
  const showTooltip =
    props.hideTooltip === undefined || props.hideTooltip === false

  const renderAvatar = useMemo(
    () => (
      // fragmentで囲うとpopoverの表示時に要素がちらついてしまうため、divで囲っている
      <div className="flex flex-shrink-0 relative">
        <div
          // popover表示用の一意なid
          id={`crew-avatar-container-${popoverTargetId}`}
          className={classNames(
            'flex items-center justify-center relative shrink-0 overflow-clip',
            props.className
          )}
          onClick={handleClickAvatar}
        >
          {props.imageURL ? (
            <CrewRetryImage
              key={props.cacheValue}
              src={targetSrc}
              alt={props.displayName}
              className={classNames(
                'object-cover',
                targetShapeClassNames,
                targetSizeClassNames
              )}
              retryType={CrewRetryImageRetryType.RetryWithSameUrl} // 取得に失敗したら同じURLでリトライする
            />
          ) : props.avatarImage ? (
            <img
              src={props.avatarImage}
              alt={props.displayName}
              className={classNames(
                'object-cover',
                targetShapeClassNames,
                targetSizeClassNames
              )}
            />
          ) : (
            // imageURL, avatarImage のどちらも設定されていない場合は、アバターの文字のみ表示
            <div className={'flex items-center justify-center'}>
              <div className="h-fit w-fit">{props.displayName.slice(0, 1)}</div>
            </div>
          )}
        </div>
        {props.isPrivate && (
          <img
            src={CrewLock}
            className="absolute -bottom-1 -right-1 crew-text-gray-2 w-4 h-4"
            alt="lock icon"
          />
        )}
        {/* 表示名・在席状態のツールチップ */}
        {showTooltip && (
          <CrewGuideToolTip
            ref={popoverRef}
            target={`#crew-avatar-container-${popoverTargetId}`}
          >
            {tooltipText}
          </CrewGuideToolTip>
        )}
      </div>
    ),
    [
      popoverTargetId,
      targetShapeClassNames,
      targetSizeClassNames,
      props.className,
      props.imageURL,
      props.cacheValue,
      props.displayName,
      props.avatarImage,
      props.isPrivate,
      handleClickAvatar,
      targetSrc,
      showTooltip,
      tooltipText,
    ]
  )

  return (
    <>
      {props?.redirectUrl ? (
        <NavLink to={props.redirectUrl} end>
          {renderAvatar}
        </NavLink>
      ) : (
        renderAvatar
      )}
    </>
  )
})
